import { NextApiRequest, NextApiResponse } from 'next';
import { ZodError } from 'zod';
import { ApiClient } from '@dozuki/web-js/utils';
import { reportError } from '~/framework';

type RequestHandler = (req: NextApiRequest, res: NextApiResponse) => any;

type HTTPMethod = 'get' | 'post' | 'patch' | 'put' | 'delete' | 'options' | 'head';

type Handlers = Partial<Record<HTTPMethod, RequestHandler>>;

type CodedError = {
   code: string;
   message?: string;
   status?: number;
};

function isCodedError(e: any): e is CodedError {
   return e && typeof e.code === 'string';
}

export function createBFFHandlers(handlers: Handlers) {
   return async (req: NextApiRequest, res: NextApiResponse) => {
      const method = req.method?.toLowerCase() as HTTPMethod;

      const handler = handlers[method];

      if (!handler) {
         return res.status(405).end(`Method ${req.method} Not Allowed`);
      }

      try {
         await handler(req, res);
      } catch (e) {
         if (ApiClient.isApiError(e)) {
            return res.status(e.response?.status || 500).send(e.response?.data);
         }

         if (isCodedError(e)) {
            return res.status(e.status ?? 500).send({ code: e.code, message: e.message });
         }

         if (e instanceof ZodError) {
            // Since we control the UI, if we see a 400 error it's probably a bug, so it should be logged.
            // We report these as warnings to reduce the alert noise from pen tests
            reportError(e, 'warning');
            return res.status(400).json({ errors: e.errors });
         }

         // Any error after this point is assumed to be unexpected and should be reported.
         reportError(e);

         return res.status(500).json({ message: 'Server Error' });
      }
   };
}
